package com.partejar.app.core.ui.theme

import androidx.compose.material3.ColorScheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.graphics.Color

private object PartejarPalette {
    val White = Color.White
    val Black = Color.Black
    val Transparent = Color.Transparent

    val LightBackground = Color(0xFFF8F4EE)
    val LightSurface = Color(0xFFFFFCF8)
    val LightSurfaceSubtle = Color(0xFFF2EBE3)
    val LightSurfaceDim = Color(0xFFECE4DB)
    val LightSurfaceLow = Color(0xFFF6F0E9)
    val LightPrimary = Color(0xFF7A4328)
    val LightPrimaryStrong = Color(0xFF58301F)
    val LightPrimaryContainer = Color(0xFFF1DED0)
    val LightAccent = Color(0xFFA95532)
    val LightAccentSubtle = Color(0xFFF6E2D6)
    val LightTextPrimary = Color(0xFF2D2925)
    val LightTextSecondary = Color(0xFF6C645D)
    val LightBorder = Color(0xFFD8CDC2)
    val LightSecondary = Color(0xFF765A49)
    val LightSecondaryContainer = Color(0xFFECE2D9)

    val DarkBackground = Color(0xFF151719)
    val DarkSurface = Color(0xFF1C1F21)
    val DarkSurfaceLow = Color(0xFF181B1D)
    val DarkSurfaceElevated = Color(0xFF24282A)
    val DarkSurfaceBright = Color(0xFF303436)
    val DarkSurfaceLowest = Color(0xFF101214)
    val DarkSurfaceHighest = Color(0xFF2C3032)
    val DarkInverseText = Color(0xFF121715)
    val DarkBorder = Color(0xFF5F482F)
    val DarkBorderSubtle = Color(0xFF3C352E)
    val DarkPrimary = Color(0xFFD79568)
    val DarkOnPrimary = Color(0xFF26170F)
    val DarkPrimaryStrong = Color(0xFFF2C5A8)
    val DarkPrimaryContainer = Color(0xFF3D281C)
    val DarkAccent = Color(0xFFE6A178)
    val DarkAccentContainer = Color(0xFF4B2D20)
    val DarkSecondary = Color(0xFFE1BBA2)
    val DarkOnSecondary = Color(0xFF2A1B14)
    val DarkTextPrimary = Color(0xFFF4EFE9)
    val DarkTextSecondary = Color(0xFFB9AFA6)

    val Error = Color(0xFFC9524C)
    val Attention = Color(0xFFB7791F)
    val Info = Color(0xFF4E7895)
    val Success = Color(0xFF4E795A)

    val LightErrorContainer = Color(0xFFF7DEDC)
    val LightOnErrorContainer = Color(0xFF5B1F1B)
    val DarkErrorContainer = Color(0xFF5A2522)

    val LightSuccessContainer = Color(0xFFE5EEE7)
    val LightOnSuccessContainer = Color(0xFF27422F)
    val DarkSuccessContainer = Color(0xFF1C2E21)
    val DarkOnSuccessContainer = Color(0xFFCDE2D2)

    val LightAttentionContainer = Color(0xFFF8E9C8)
    val LightOnAttentionContainer = Color(0xFF5A3A0F)
    val DarkAttentionContainer = Color(0xFF38260B)
    val DarkOnAttentionContainer = Color(0xFFF3D89B)

    val LightInfoContainer = Color(0xFFE3EDF3)
    val LightOnInfoContainer = Color(0xFF29485C)
    val DarkInfoContainer = Color(0xFF1B2C38)
    val DarkOnInfoContainer = Color(0xFFC9DFEC)
}

val PartejarLightColorScheme: ColorScheme = lightColorScheme(
    primary = PartejarPalette.LightPrimary,
    onPrimary = PartejarPalette.White,
    primaryContainer = PartejarPalette.LightPrimaryContainer,
    onPrimaryContainer = PartejarPalette.LightPrimaryStrong,
    inversePrimary = PartejarPalette.DarkPrimary,
    secondary = PartejarPalette.LightSecondary,
    onSecondary = PartejarPalette.White,
    secondaryContainer = PartejarPalette.LightSecondaryContainer,
    onSecondaryContainer = PartejarPalette.LightTextPrimary,
    tertiary = PartejarPalette.LightAccent,
    onTertiary = PartejarPalette.White,
    tertiaryContainer = PartejarPalette.LightAccentSubtle,
    onTertiaryContainer = PartejarPalette.LightTextPrimary,
    background = PartejarPalette.LightBackground,
    onBackground = PartejarPalette.LightTextPrimary,
    surface = PartejarPalette.LightSurface,
    onSurface = PartejarPalette.LightTextPrimary,
    surfaceVariant = PartejarPalette.LightSurfaceSubtle,
    onSurfaceVariant = PartejarPalette.LightTextSecondary,
    surfaceTint = PartejarPalette.Transparent,
    inverseSurface = PartejarPalette.LightTextPrimary,
    inverseOnSurface = PartejarPalette.LightBackground,
    error = PartejarPalette.Error,
    onError = PartejarPalette.White,
    errorContainer = PartejarPalette.LightErrorContainer,
    onErrorContainer = PartejarPalette.LightOnErrorContainer,
    outline = PartejarPalette.LightBorder,
    outlineVariant = PartejarPalette.LightSurfaceSubtle,
    scrim = PartejarPalette.Black,
    surfaceBright = PartejarPalette.LightSurface,
    surfaceDim = PartejarPalette.LightSurfaceDim,
    surfaceContainerLowest = PartejarPalette.LightSurface,
    surfaceContainerLow = PartejarPalette.LightSurfaceLow,
    surfaceContainer = PartejarPalette.LightSurfaceSubtle,
    surfaceContainerHigh = PartejarPalette.LightSurfaceDim,
    surfaceContainerHighest = PartejarPalette.LightBorder
)

val PartejarDarkColorScheme: ColorScheme = darkColorScheme(
    primary = PartejarPalette.DarkPrimary,
    onPrimary = PartejarPalette.DarkOnPrimary,
    primaryContainer = PartejarPalette.DarkPrimaryContainer,
    onPrimaryContainer = PartejarPalette.DarkPrimaryStrong,
    inversePrimary = PartejarPalette.LightAccent,
    secondary = PartejarPalette.DarkSecondary,
    onSecondary = PartejarPalette.DarkOnSecondary,
    secondaryContainer = PartejarPalette.DarkSurfaceElevated,
    onSecondaryContainer = PartejarPalette.DarkTextPrimary,
    tertiary = PartejarPalette.DarkAccent,
    onTertiary = PartejarPalette.DarkInverseText,
    tertiaryContainer = PartejarPalette.DarkAccentContainer,
    onTertiaryContainer = PartejarPalette.LightAccentSubtle,
    background = PartejarPalette.DarkBackground,
    onBackground = PartejarPalette.DarkTextPrimary,
    surface = PartejarPalette.DarkSurface,
    onSurface = PartejarPalette.DarkTextPrimary,
    surfaceVariant = PartejarPalette.DarkSurfaceElevated,
    onSurfaceVariant = PartejarPalette.DarkTextSecondary,
    surfaceTint = PartejarPalette.Transparent,
    inverseSurface = PartejarPalette.DarkTextPrimary,
    inverseOnSurface = PartejarPalette.DarkInverseText,
    error = PartejarPalette.Error,
    onError = PartejarPalette.White,
    errorContainer = PartejarPalette.DarkErrorContainer,
    onErrorContainer = PartejarPalette.LightErrorContainer,
    outline = PartejarPalette.DarkBorder,
    outlineVariant = PartejarPalette.DarkBorderSubtle,
    scrim = PartejarPalette.Black,
    surfaceBright = PartejarPalette.DarkSurfaceBright,
    surfaceDim = PartejarPalette.DarkBackground,
    surfaceContainerLowest = PartejarPalette.DarkSurfaceLowest,
    surfaceContainerLow = PartejarPalette.DarkSurfaceLow,
    surfaceContainer = PartejarPalette.DarkSurface,
    surfaceContainerHigh = PartejarPalette.DarkSurfaceElevated,
    surfaceContainerHighest = PartejarPalette.DarkSurfaceHighest
)

@Immutable
data class PartejarExtraColors(
    val success: Color,
    val onSuccess: Color,
    val successContainer: Color,
    val onSuccessContainer: Color,
    val attention: Color,
    val onAttention: Color,
    val attentionContainer: Color,
    val onAttentionContainer: Color,
    val info: Color,
    val onInfo: Color,
    val infoContainer: Color,
    val onInfoContainer: Color
)

internal val PartejarLightExtraColors = PartejarExtraColors(
    success = PartejarPalette.Success,
    onSuccess = PartejarPalette.White,
    successContainer = PartejarPalette.LightSuccessContainer,
    onSuccessContainer = PartejarPalette.LightOnSuccessContainer,
    attention = PartejarPalette.Attention,
    onAttention = PartejarPalette.White,
    attentionContainer = PartejarPalette.LightAttentionContainer,
    onAttentionContainer = PartejarPalette.LightOnAttentionContainer,
    info = PartejarPalette.Info,
    onInfo = PartejarPalette.White,
    infoContainer = PartejarPalette.LightInfoContainer,
    onInfoContainer = PartejarPalette.LightOnInfoContainer
)

internal val PartejarDarkExtraColors = PartejarExtraColors(
    success = PartejarPalette.Success,
    onSuccess = PartejarPalette.White,
    successContainer = PartejarPalette.DarkSuccessContainer,
    onSuccessContainer = PartejarPalette.DarkOnSuccessContainer,
    attention = PartejarPalette.Attention,
    onAttention = PartejarPalette.White,
    attentionContainer = PartejarPalette.DarkAttentionContainer,
    onAttentionContainer = PartejarPalette.DarkOnAttentionContainer,
    info = PartejarPalette.Info,
    onInfo = PartejarPalette.White,
    infoContainer = PartejarPalette.DarkInfoContainer,
    onInfoContainer = PartejarPalette.DarkOnInfoContainer
)

internal val LocalPartejarExtraColors = staticCompositionLocalOf {
    PartejarLightExtraColors
}
